import { lazy, Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAdoptedTools, groupToolsForDock, iconFor, toolName } from "../../../marketplace";
import ToolDock, { type ToolDockGroup } from "../../../../shared/ui/ToolDock";
import { ConstellationLaunchSheet, ConstellationOpenBoardModal, ConstellationSessionsModal, ConstellationScenesModal, BoardDefaultsModal, loadConstellationFigureManager } from "../../../constellation";
import { SchedulerDockActions } from "../../../scheduler";
import { PaymentsDockActions } from "../../../payments";
import { SystemicMapDockActions } from "../../../systemic_map";
import { MeditationDockActions } from "../../../meditation";
import { EmdrDockActions } from "../../../emdr";

const ConstellationFigureManager = lazy(loadConstellationFigureManager);

/**
 * The tools this dock knows how to open.
 *
 * Adopting a tool the dock has no action for would otherwise put a button here
 * that does nothing.
 */
const DOCKED_SLUGS = new Set([
    "constellation",
    "scheduler",
    "payments",
    "genogram-ecomap",
    "meditation",
    "emdr",
]);

export default function HomeToolDock() {
    const { t } = useTranslation();
    const { data: tools, isLoading } = useAdoptedTools();
    const [openSlug, setOpenSlug] = useState<string | null>(null);
    const [sessionsOpen, setSessionsOpen] = useState(false);
    const [scenesOpen, setScenesOpen] = useState(false);
    const [defaultsOpen, setDefaultsOpen] = useState(false);
    const [openBoardOpen, setOpenBoardOpen] = useState(false);
    const [figuresOpen, setFiguresOpen] = useState(false);

    /**
     * Grouped rather than listed against a hand-written order.
     *
     * With four tools a flat row stops saying anything: Agenda, Pagos,
     * Constelaciones and Genograma sit side by side with no reason for the
     * order they are in. Administration — the things reached between patients
     * — comes first, then the clinical instruments gathered by the way of
     * working they belong to. A fifth tool needs a catalogue row, not an edit
     * here.
     */
    const groups: ToolDockGroup[] = useMemo(() => {
        if (!tools) return [];
        return groupToolsForDock(
            tools.filter((tool) => DOCKED_SLUGS.has(tool.slug)),
        ).map((group) => ({
            key: group.key,
            label: t(group.labelKey),
            shortLabel: group.shortLabelKey ? t(group.shortLabelKey) : undefined,
            items: group.tools.map((tool) => {
                const Icon = iconFor(tool.icon);
                return {
                    id: tool.id,
                    label: toolName(t, tool),
                    icon: <Icon className="h-5 w-5" />,
                    onSelect: () => setOpenSlug(tool.slug),
                };
            }),
        }));
    }, [tools, t]);

    if (isLoading || !tools) return null;

    const adopted = (slug: string) => tools.some((tool) => tool.slug === slug);
    const close = () => setOpenSlug(null);

    return (
        <>
            <ToolDock
                groups={groups}
                ariaLabel={t("constellation.dock.title")}
            />

            <ConstellationLaunchSheet
                isOpen={openSlug === "constellation"}
                onClose={close}
                onOpenBoard={() => setOpenBoardOpen(true)}
                onShowSessions={() => setSessionsOpen(true)}
                onShowScenes={() => setScenesOpen(true)}
                onShowDefaults={() => setDefaultsOpen(true)}
                onManageFigures={() => setFiguresOpen(true)}
            />
            <ConstellationOpenBoardModal
                isOpen={openBoardOpen}
                onClose={() => setOpenBoardOpen(false)}
            />
            <ConstellationSessionsModal
                isOpen={sessionsOpen}
                onClose={() => setSessionsOpen(false)}
            />
            <ConstellationScenesModal
                isOpen={scenesOpen}
                onClose={() => setScenesOpen(false)}
            />
            <BoardDefaultsModal
                isOpen={defaultsOpen}
                onClose={() => setDefaultsOpen(false)}
            />
            {figuresOpen && (
                <Suspense fallback={null}>
                    <ConstellationFigureManager onClose={() => setFiguresOpen(false)} />
                </Suspense>
            )}

            {/* Mounted only once the tool is adopted, so their queries never
                run for a therapist who does not have it. */}
            {adopted("scheduler") && (
                <SchedulerDockActions
                    isOpen={openSlug === "scheduler"}
                    onClose={close}
                />
            )}
            {adopted("payments") && (
                <PaymentsDockActions isOpen={openSlug === "payments"} onClose={close} />
            )}
            {adopted("genogram-ecomap") && (
                <SystemicMapDockActions
                    isOpen={openSlug === "genogram-ecomap"}
                    onClose={close}
                />
            )}
            {adopted("meditation") && (
                <MeditationDockActions
                    isOpen={openSlug === "meditation"}
                    onClose={close}
                />
            )}
            {adopted("emdr") && (
                <EmdrDockActions isOpen={openSlug === "emdr"} onClose={close} />
            )}
        </>
    );
}
